/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { motion, Variants } from "framer-motion";
import { FaCalculator, FaHandshake, FaLock, FaComments } from "react-icons/fa";
import { useHeroSection } from "@/hooks/useHeroSection";
import Navbar from "@/shared/Navbar";
import axios from "axios";
import DigitalMortgageCoachForm from "./DigitalMortgageCoachForm";
import { usePages } from "@/hooks/usePages";

// import Navbar from "@/shared/Navbar";

const Page = () => {
  const [playVideo, setPlayVideo] = useState(false);
  const { data: pages, loading } = usePages(); // ✅ dynamic fetch
  // ✅ Typed animation variants
  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };
  const HeroSection = useHeroSection({
    title: " Request a Total Cost Analysis",
    subtitle:
      "AskLocal brings everything people, process, and numbers  together so your move is simple, transparent, and cost-efficient. No spam, no pressure just clarity for your next move.",
    buttonText: "Get Coach",
    backgroundImage:
      "/askLocal-digital-mortgage-coach/askLocal-digital-mortgage-coach-hero.png",
    buttonLink: "#mortgage-coach-form-section",
  });
  const howToHubDropdown = useMemo(() => {
    const staticPages = [
      {
        label: "Property Intelligence Report",
        href: "/how-to-hub/property-intelligence-report",
      },
      { label: "Mortgage Guide", href: "/how-to-hub/mortgage-guide" },
    ];

    const dynamicPages =
      pages?.map((p: any) => ({
        label: p.title || "Untitled",
        href: `/how-to-hub/${p.slug}`,
      })) || [];

    return [
      ...(dynamicPages.length
        ? dynamicPages
        : loading
        ? [{ label: "Loading...", href: "#" }]
        : []),
      ...staticPages,
    ];
  }, [pages, loading]);

  return (
    <>
      <Navbar
        defaultLogo="/logo/asklocal-white-logo.png"
        scrolledLogo="/logo/color-full-logo.png"
        isLight
        labelColor="#ffffff"
        labelHoverColor="#EB9C2C"
        buttonBg="#EB9C2C"
        buttonTextColor="#FFFFFF"
        buttonHoverBg="#4177BC"
        buttonHoverText="#ffffff"
        ctaText="Looking to Buy/Sell?"
        ctaLink="/buy-sell"
        navItems={[
          { label: "Home", href: "/" },
          {
            label: "Build Your Buying Power",
            href: "/askLocal-digital-mortgage-coach",
          },
          {
            label: "Property Reports",
            href: "/how-to-hub/property-intelligence-report",
          },
          {
            label: "How-To-Hub",
            dropdown: howToHubDropdown, // ✅ dynamic + static pages
          },
          { label: "Log In", href: "https://portal.asklocal.com/" },
        ]}
      />

      <div className="w-full text-gray-800">
        {/* ---------------- HERO SECTION ---------------- */}
        {HeroSection}

        {/* ---------------- FORM SECTION ---------------- */}
        <section className="bg-white text-center py-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-[1300px] mx-auto px-4 sm:px-6"
          >
            <h2 className="text-[36px] sm:text-[50px] md:text-[68px] lg:text-[78px] mx-auto text-[#000000] judson-regular mb-6 leading-tight">
              Simplify Your Home Buying <br className="hidden sm:block" />{" "}
              Journey With AskLocal&apos;s Digital{" "}
              <br className="hidden sm:block" /> Mortgage Coach!
            </h2>
            <p
              id="mortgage-coach-form-section"
              className="text-[#938E8E] mb-12 inter-regular text-sm sm:text-base"
            >
              Get a Free, Comprehensive Total Cost Analysis with up to 4
              industry loan <br className="hidden sm:block" /> options Compare
              side by side.
            </p>

            <div className="flex flex-col lg:flex-row gap-10 items-center justify-center">
              {/* Left - Video/Thumbnail */}
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="w-full sm:w-[80%] md:w-[70%] lg:w-1/2 relative"
              >
                {!playVideo ? (
                  <div
                    className="relative cursor-pointer"
                    onClick={() => setPlayVideo(true)}
                  >
                    <Image
                      src="/askLocal-digital-mortgage-coach/vedio.png"
                      alt="Video Thumbnail"
                      width={612}
                      height={528}
                      className="rounded-xl lg:h-[528px] shadow-lg w-full object-cover"
                    />

                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-16 sm:w-20 h-16 sm:h-20 text-white bg-[#4177BC] rounded-full p-4 shadow-lg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </motion.div>
                  </div>
                ) : (
                  <div className="w-full aspect-video rounded-xl overflow-hidden shadow-lg">
                    <iframe
                      src="https://www.youtube.com/embed/_8oc9QWW_uU?autoplay=1"
                      title="YouTube video player"
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    ></iframe>
                  </div>
                )}
              </motion.div>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="w-full sm:w-[80%] md:w-[70%] lg:w-[500px] space-y-10"
              >
                <DigitalMortgageCoachForm />
              </motion.div>
            </div>
          </motion.div>
        </section>

        {/* ---------------- FEATURES SECTION ---------------- */}
        <section className="bg-[#F9FAFB] py-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-[1330px] mx-auto px-4 sm:px-6"
          >
            <h2 className="text-[36px] sm:text-[48px] md:text-[68px] leading-tight text-[#000000] judson-regular mb-5">
              Powerful Tools To Simplify <br className="hidden sm:block" /> Your
              Home Financing Journey
            </h2>
            <p className="text-[#6B7280] text-sm sm:text-base mb-14 inter-regular">
              Explore intelligent features designed to make mortgage planning
              effortless — from instant <br className="hidden sm:block" />{" "}
              calculations to personalized guidance and secure tracking.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {[
                {
                  icon: <FaCalculator className="text-[40px]" />,
                  title: "Instant Loan Calculator",
                  desc: "Estimate your monthly payments, interest, and total costs within seconds.",
                },
                {
                  icon: <FaHandshake className="text-[40px]" />,
                  title: "Personalized Recommendations",
                  desc: "Get smart suggestions based on your unique financial situation and property goals.",
                },
                {
                  icon: <FaLock className="text-[40px]" />,
                  title: "Secure Pre-Approval Tracking",
                  desc: "Monitor your pre-approval status and lender communications in one secure dashboard.",
                },
                {
                  icon: <FaComments className="text-[40px]" />,
                  title: "Expert Chat Support",
                  desc: "Need help understanding your options? Connect directly with verified mortgage advisors.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                  className="
           bg-white rounded-xl shadow-md hover:shadow-lg 
            transition-all p-6 text-left flex flex-col gap-4
            h-60 sm:h-[260px] md:h-auto
          "
                >
                  {/* Icon - Left aligned */}
                  <div className="w-16 h-16 bg-[#4177BC] flex items-center justify-center rounded-xl text-white shadow-sm">
                    {item.icon}
                  </div>

                  {/* Title */}
                  <h3 className="text-[22px] sm:text-[26px] md:text-[28px] judson-bold text-[#000000] leading-snug">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[14px] sm:text-[15px] md:text-[16px] text-[#716B6B] inter-regular leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Page;
